import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, User, Building2, Calendar, CreditCard, CheckCircle, AlertCircle, Loader2, ArrowRight, Mail, Phone, Users, FileText, ArrowLeft } from 'lucide-react';
import { lookupMember } from '../services/api';
import { getText, getCurrentLanguage } from '../services/dataSync';
import toast from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';

const RenewalPage = () => {
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [searching, setSearching] = useState(false);
  const [member, setMember] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Listen for language changes
  React.useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchIdentifier.trim()) {
      setError(getText('renewal.searchError') || 'Please enter your customer ID or email');
      return;
    }

    setSearching(true);
    setError('');
    setMember(null);

    try {
      const memberData = await lookupMember(searchIdentifier.trim());
      setMember(memberData);
    } catch (err) {
      setError(err.message || getText('renewal.notFound') || 'Member not found. Please check your customer ID or email.');
      setMember(null);
    } finally {
      setSearching(false);
    }
  };

  const handleRenew = async () => {
    if (!member || !member._id) {
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmRenewal = async () => {
    if (!member || !member._id) {
      return;
    }

    setRenewing(true);
    setError('');

    try {
      toast.success(getText('renewal.renewalSuccess') || 'Redirecting to registration and payment...');
      const planId = member.membershipPlanId?._id || member.membershipPlanId || '';
      const workerQ =
        member.workerCount != null && member.workerCount !== ''
          ? `&workerCount=${encodeURIComponent(String(member.workerCount))}`
          : '';
      setShowConfirmModal(false);
      navigate(
        `/member-registration?renewal=true&memberId=${member._id}&planId=${planId}&planName=${encodeURIComponent(member.membershipPlanName || '')}${workerQ}`
      );
    } catch (err) {
      const errorMessage = err?.message || err?.data?.message || getText('renewal.renewalError') || 'Failed to continue renewal. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setRenewing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const locale = currentLang === 'hi' ? 'hi-IN' : currentLang === 'mr' ? 'mr-IN' : 'en-US';
    return date.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    return days !== null && days > 0 && days <= 30;
  };

  return (
    <>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmRenewal}
        title={getText('renewal.confirmTitle') || 'Confirm Renewal'}
        message={getText('renewal.confirmMessage') || 'Are you sure you want to renew your membership? You will be redirected to payment.'}
        confirmText={getText('renewal.confirm') || 'Yes, Renew'}
        cancelText={getText('renewal.cancel') || 'Cancel'}
        type="warning"
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Back Button */}
          <div className="mb-6">
            <div className="mb-4">
              <Link
                to="/membership"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">Back to Membership</span>
              </Link>
            </div>
          </div>
          
          {/* Title Section */}
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {getText('renewal.title') || 'Renew Your Membership'}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {getText('renewal.subtitle') || 'Enter your customer ID or email to find your membership plan and renew it'}
            </p>
            <p className="text-sm text-gray-500 max-w-2xl mx-auto mt-3">
              {getText('renewal.privacyNote') || 'For your privacy, email and phone are shown partially masked after lookup.'}
            </p>
          </div>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {getText('renewal.searchLabel') || 'Customer ID or Email'}
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchIdentifier}
                    onChange={(e) => setSearchIdentifier(e.target.value)}
                    placeholder={getText('renewal.searchPlaceholder') || 'Enter customer ID or email to find your plan'}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900"
                    disabled={searching}
                  />
                </div>
                <button
                  type="submit"
                  disabled={searching}
                  className="px-8 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {searching ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {getText('renewal.searching') || 'Searching...'}
                    </>
                  ) : (
                    <>
                      <Search className="h-5 w-5" />
                      {getText('renewal.search') || 'Search'}
                    </>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </form>
        </div>

        {/* Member Details Card */}
        {member && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {getText('renewal.memberDetails') || 'Member Details'}
              </h2>
              {member.approvalStatus === 'Approved' && (
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  member.membershipStatus === 'Active' 
                    ? 'bg-green-100 text-green-700' 
                    : member.membershipStatus === 'Expired'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {member.membershipStatus}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="flex items-start gap-3">
                <div className="p-3 bg-primary-100 rounded-xl">
                  <User className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{getText('renewal.memberName') || 'Member Name'}</p>
                  <p className="font-semibold text-gray-900">{member.personName}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Building2 className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{getText('renewal.organization') || 'Organization'}</p>
                  <p className="font-semibold text-gray-900">{member.organizationName}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{getText('renewal.email') || 'Email'}</p>
                  <p className="font-semibold text-gray-900">{member.email}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Phone className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{getText('renewal.mobile') || 'Mobile'}</p>
                  <p className="font-semibold text-gray-900">{member.mobile}</p>
                </div>
              </div>

              {member.customId && (
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <CreditCard className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{getText('renewal.memberId') || 'Member ID'}</p>
                    <p className="font-semibold text-gray-900">{member.customId}</p>
                  </div>
                </div>
              )}

              {member.membershipPlanName && (
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-indigo-100 rounded-xl">
                    <CreditCard className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{getText('renewal.currentPlan') || 'Current Plan'}</p>
                    <p className="font-semibold text-gray-900">{member.membershipPlanName}</p>
                  </div>
                </div>
              )}

              {member.workerCount && (
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-teal-100 rounded-xl">
                    <Users className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Number of Workers</p>
                    <p className="font-semibold text-gray-900">{member.workerCount}</p>
                  </div>
                </div>
              )}

              {member.calculatedMembershipFee && (
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <CreditCard className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Membership Fee</p>
                    <p className="font-semibold text-gray-900">₹{Number(member.calculatedMembershipFee).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Expiry Date Section */}
            {member.expiryDate && (
              <div className={`p-6 rounded-xl mb-6 ${
                isExpired(member.expiryDate)
                  ? 'bg-red-50 border-2 border-red-200'
                  : isExpiringSoon(member.expiryDate)
                  ? 'bg-yellow-50 border-2 border-yellow-200'
                  : 'bg-green-50 border-2 border-green-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{getText('renewal.expiryDate') || 'Membership Expiry Date'}</p>
                    <p className="text-2xl font-bold text-gray-900">{formatDate(member.expiryDate)}</p>
                    {!isExpired(member.expiryDate) && getDaysUntilExpiry(member.expiryDate) !== null && (
                      <p className="text-sm text-gray-600 mt-2">
                        {getDaysUntilExpiry(member.expiryDate) > 0 
                          ? `${getDaysUntilExpiry(member.expiryDate)} ${getText('renewal.daysRemaining') || 'days remaining'}`
                          : getText('renewal.expired') || 'Expired'}
                      </p>
                    )}
                  </div>
                  <div className="p-4 bg-white rounded-xl">
                    <Calendar className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
              </div>
            )}

            {/* Renewal Button */}
            {member.approvalStatus === 'Approved' && (
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleRenew}
                  disabled={renewing}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {renewing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{getText('renewal.processing') || 'Processing...'}</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      <span>{getText('renewal.renewButton') || 'Renew Membership'}</span>
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
                <button
                  onClick={() => navigate('/membership')}
                  className="px-6 py-4 border-2 border-primary-600 text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors"
                >
                  {getText('renewal.viewPlans') || 'View Plans'}
                </button>
              </div>
            )}

            {member.approvalStatus !== 'Approved' && (
              <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                  {getText('renewal.pendingApproval') || 'Your membership is pending approval. Please contact support for assistance.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-primary-50 rounded-2xl shadow-lg p-6 border border-primary-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {getText('renewal.needHelp') || 'Need Help?'}
          </h3>
          <p className="text-gray-600 mb-4">
            {getText('renewal.helpText') || 'If you cannot find your membership or need assistance with renewal, please contact our support team.'}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 text-white hover:text-white rounded-xl font-semibold hover:from-primary-700 hover:via-primary-800 hover:to-primary-900 transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5 transform"
            >
              <Mail className="h-5 w-5 text-white" />
              <span className="text-white">{getText('renewal.contactSupport') || 'Contact Support'}</span>
              <ArrowRight className="h-4 w-4 text-white transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default RenewalPage;
